import { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Button from '../ui/Button';
import Card from '../ui/Card';

/**
 * QR Code Generator Component
 * Generates QR code for session joining
 */

const QRCodeGenerator = ({ sessionCode, sessionUrl, sessionName, size = 256, showDownload = true }) => {
  const [downloadFormat, setDownloadFormat] = useState('png');
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);

  // Generate join URL (or use provided URL)
  const joinUrl = sessionUrl || `${window.location.origin}/join/${sessionCode}`;

  // Download QR code as image
  const handleDownload = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    canvas.width = size;
    canvas.height = size;

    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0);

      if (downloadFormat === 'png') {
        const pngFile = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.download = `session-${sessionCode}-qr.png`;
        downloadLink.href = pngFile;
        downloadLink.click();
      } else if (downloadFormat === 'svg') {
        const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        downloadLink.download = `session-${sessionCode}-qr.svg`;
        downloadLink.href = url;
        downloadLink.click();
        URL.revokeObjectURL(url);
      }
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Print QR code
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${sessionName || sessionCode}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            }
            .container {
              text-align: center;
              padding: 40px;
            }
            h1 {
              font-size: 24px;
              margin-bottom: 10px;
              color: #1f2937;
            }
            .code {
              font-size: 32px;
              font-weight: bold;
              color: #3b82f6;
              margin-bottom: 20px;
            }
            .qr-code {
              margin: 20px 0;
            }
            .instructions {
              margin-top: 20px;
              font-size: 14px;
              color: #6b7280;
            }
            @media print {
              @page {
                margin: 20mm;
              }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>${sessionName || 'Training Session'}</h1>
            <div class="code">Code: ${sessionCode}</div>
            <div class="qr-code">${svgData}</div>
            <div class="instructions">
              <p><strong>How to Join:</strong></p>
              <p>1. Scan this QR code with your phone camera</p>
              <p>2. Or visit: <strong>${window.location.origin}/join</strong></p>
              <p>3. Enter code: <strong>${sessionCode}</strong></p>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Copy URL to clipboard with fallback for non-HTTPS
  const handleCopyUrl = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(joinUrl);
      } else {
        // Fallback for non-HTTPS contexts
        const textArea = document.createElement('textarea');
        textArea.value = joinUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Try fallback even if main method fails
      try {
        const textArea = document.createElement('textarea');
        textArea.value = joinUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy also failed:', fallbackErr);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* QR Code Display */}
      <div className="flex justify-center p-6 bg-white rounded-lg border-2 border-neutral-200">
        <div ref={qrRef} className="flex flex-col items-center">
          <QRCodeSVG
            value={joinUrl}
            size={size}
            level="H"
            includeMargin={true}
            imageSettings={{
              src: '', // Could add logo here
              height: 0,
              width: 0,
              excavate: true,
            }}
          />
          <div className="mt-4 text-center">
            <p className="text-sm font-semibold text-neutral-700">Session Code</p>
            <p className="text-2xl font-bold text-primary-600 tracking-wider">{sessionCode}</p>
          </div>
        </div>
      </div>

      {/* Join URL */}
      <Card padding="sm" className="bg-neutral-50">
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-neutral-600 mb-1">Join URL:</p>
            <p className="text-sm text-neutral-900 truncate">{joinUrl}</p>
          </div>
          <Button variant={copied ? "primary" : "ghost"} size="sm" onClick={handleCopyUrl}>
            {copied ? (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                Copy
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* Actions */}
      {showDownload && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <select
              value={downloadFormat}
              onChange={(e) => setDownloadFormat(e.target.value)}
              className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="png">PNG Image</option>
              <option value="svg">SVG Vector</option>
            </select>
            <Button variant="secondary" onClick={handleDownload}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Download QR
            </Button>
          </div>

          <Button variant="ghost" fullWidth onClick={handlePrint}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
              />
            </svg>
            Print QR Code
          </Button>
        </div>
      )}

      {/* Instructions */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">📱 How participants can join:</h4>
        <ol className="text-xs text-blue-800 space-y-1 ml-4 list-decimal">
          <li>Open their phone camera and point at the QR code</li>
          <li>Tap the notification to open the join page</li>
          <li>Or manually visit the join URL and enter the code</li>
          <li>Enter their name and join the session</li>
        </ol>
      </div>
    </div>
  );
};

export default QRCodeGenerator;
